import { memo } from "react";
import useEvent from "react-use-event-hook";

import { MapLibreAutoPanPopup, MapLibreStationPinMarker } from "@ryde/controls";
import { IStationDto, MarkerIconType } from "@ryde/shared";

import { StationsMapStationPopup } from "./stations-map-station-popup";

interface IStationsMapMarkerProps {
  station: IStationDto;
  latitude: number;
  longitude: number;
  /**
   * Primitives rather than a pin object: a fresh `{ type, color }` each render would defeat the memo
   * below, and on a select-all that means re-rendering every station's SVG on every hover.
   */
  pinType: MarkerIconType;
  pinColor: string;
  isPopupOpen: boolean;
  onClick: (stationId: number) => void;
  onHover: (stationId: number) => void;
  onLeave: (stationId: number) => void;
  onPopupEnter: (stationId: number) => void;
  onPopupLeave: (stationId: number) => void;
  onPopupClose: (stationId: number) => void;
}

/**
 * One station pin on the stations map, with the hover popup describing it.
 *
 * Memoized, and every callback prop takes the station id so the parent can hand down one stable handler
 * each. Selecting every row puts thousands of these on the map, and without this a single hover — which
 * only changes which popup is open — re-rendered all of them.
 */
export const StationsMapMarker = memo<IStationsMapMarkerProps>(
  ({
    station,
    latitude,
    longitude,
    pinType,
    pinColor,
    isPopupOpen,
    onClick,
    onHover,
    onLeave,
    onPopupEnter,
    onPopupLeave,
    onPopupClose,
  }) => {
    const handleClick = useEvent((event: { originalEvent: MouseEvent }) => {
      event.originalEvent.stopPropagation();
      onClick(station.id);
    });

    const areaName = station.areaName ?? "";
    const markerTitle = `${areaName ? `${areaName}, ` : ""}${station.name ?? ""} (${station.id})`;

    return (
      <>
        <MapLibreStationPinMarker
          latitude={latitude}
          longitude={longitude}
          type={pinType}
          color={pinColor}
          onClick={handleClick}
          // Suppressed while the popup is open, or the browser's own tooltip covers it.
          title={isPopupOpen ? undefined : markerTitle}
          actionLabel={markerTitle}
          onMouseEnter={() => onHover(station.id)}
          onMouseLeave={() => onLeave(station.id)}
        />

        <MapLibreAutoPanPopup
          isOpen={isPopupOpen}
          latitude={latitude}
          longitude={longitude}
          hasCloseButton={false}
          shouldCloseOnMapClick={false}
          onClose={() => onPopupClose(station.id)}
          onMouseEnter={() => onPopupEnter(station.id)}
          onMouseLeave={() => onPopupLeave(station.id)}
        >
          <StationsMapStationPopup station={station} />
        </MapLibreAutoPanPopup>
      </>
    );
  },
);

StationsMapMarker.displayName = "StationsMapMarker";
